import * as Avatar from '@radix-ui/react-avatar'
import { IconUser, IconUserFilled } from '@tabler/icons-react'
import { Ref } from 'react'

import { BGColorClass, BodyVariant } from '../../types/types'
import { AllowedIcon, AstraIcon } from '../AstraIcon/AstraIcon'
import { AstraParagraph } from '../Typography/AstraParagraph/AstraParagraph'
import { getTextColorByContrast, stringToLCH, stringToLchColor } from '../Utilities/colorHelpers'

type AvatarSizes = 'xsmall' | 'small' | 'medium' | 'large'
type BorderRadius = 'full' | 'small'

type AvatarBaseProps = {
    borderRadius?: BorderRadius
    size?: AvatarSizes
    bgOverride?: BGColorClass | '' // defaults to 'bg-neutral-rest'
    useColorGenerator?: boolean // If true, generates a color based on initials
    chroma?: number // Saturation for color generation, default is 100
    luminance?: number // Lightness for color generation, default is 85
}

type AvatarWithImageProps = {
    imageUrl: string
    variant: 'image'
    initials?: string
} & AvatarBaseProps

type AvatarWithIconProps = {
    variant: 'icon'
    initials?: string
} & AvatarBaseProps

type AvatarWithInitialsProps = {
    initials?: string
    variant: 'initial'
} & AvatarBaseProps

const sizeStyles: Record<AvatarSizes, string> = {
    xsmall: 'w-4 h-4',
    small: 'w-5 h-5',
    medium: 'w-7 h-7',
    large: 'w-9 h-9',
}

const iconSizeMap: Record<AvatarSizes, 'small' | 'medium' | 'large'> = {
    xsmall: 'small',
    small: 'small',
    medium: 'medium',
    large: 'large',
}

const bodyVariant: Record<AvatarSizes, BodyVariant> = {
    xsmall: 'body-extra-small',
    small: 'body-extra-small',
    medium: 'body-small',
    large: 'body-large',
}

const borderRadiusStyle: Record<BorderRadius, string> = {
    full: 'rounded-full',
    small: 'rounded',
}

export type AstraAvatarProps = (AvatarWithImageProps | AvatarWithIconProps | AvatarWithInitialsProps) & {
    ref?: Ref<HTMLSpanElement>
}

const baseStyles = 'inline-flex select-none items-center justify-center'

/**
 * Avatar identifies a person or entity compactly, by image, initials or a generic icon. `variant` selects
 * which of the three to render; `useColorGenerator` derives a stable background from the initials so the same
 * person looks the same everywhere.
 *
 * @summary for identifying a person by image, initials or icon
 */
export const AstraAvatar = ({
    ref,
    size = 'medium',
    borderRadius = 'full',
    bgOverride,
    useColorGenerator,
    chroma = 40,
    luminance = 55,
    initials,
    ...props
}: AstraAvatarProps) => {
    const generatedBg = !bgOverride && useColorGenerator ? stringToLchColor(initials ?? 'test', chroma, luminance) : ''
    const fallbackBG = !useColorGenerator && !bgOverride ? 'bg-neutral-rest' : bgOverride || 'bg-neutral-rest'
    const bgToUse = !generatedBg ? fallbackBG : ''

    // Initials and icons share one foreground color, so the avatar looks the same whichever fallback renders.
    // Keyed off the same string as generatedBg above, or the contrast pick drifts from the background it is meant to match.
    const generatedFg = generatedBg ? getTextColorByContrast(stringToLCH(initials ?? 'test', chroma, luminance)) : ''
    const fgStyle = generatedFg ? { color: generatedFg } : undefined
    const fgClass = generatedFg ? '' : 'text-default'

    const fallbackIcon = (icon: AllowedIcon) => (
        <span className={`flex ${fgClass}`} style={fgStyle}>
            <AstraIcon icon={icon} size={iconSizeMap[size]} />
        </span>
    )

    return (
        <Avatar.Root ref={ref} className="flex items-center justify-center">
            {props.variant === 'image' && props.imageUrl && (
                <Avatar.Image src={props.imageUrl} className={`${baseStyles} ${borderRadiusStyle[borderRadius]} ${sizeStyles[size]} ${bgToUse}`} />
            )}
            <Avatar.Fallback
                className={`${baseStyles} ${borderRadiusStyle[borderRadius]} ${sizeStyles[size]} ${bgToUse}`}
                style={generatedBg ? { backgroundColor: generatedBg } : undefined}
            >
                {props.variant === 'icon' && fallbackIcon(IconUserFilled)}
                {props.variant === 'initial' &&
                    (initials ? (
                        <AstraParagraph weight="strong" variant={bodyVariant[size]} className={`uppercase ${fgClass}`} style={fgStyle}>
                            {initials}
                        </AstraParagraph>
                    ) : (
                        fallbackIcon(IconUser)
                    ))}
            </Avatar.Fallback>
        </Avatar.Root>
    )
}
